import { selectShus2HallReportSearchCondition } from '../../utils/shu';
import { isGraphDateType } from '../dataKiGraph';
import {
  DataModelTransitionData,
  DataModelTransitionParams,
} from '../dataModelTransition';
import {
  DataModelTransitionGraphParams,
  DataModelTransitionGraphSetting,
  ModelTransitionGraphSeriesType,
} from '../dataModelTransitionGraph';
import { MAIN_FIELD_TYPE, MainFieldType, Option } from '../schemas';
import { ShuOption } from '../shu';

export const MODEL_TRANSITION_OUTPUT_TYPE_OPTIONS = [
  { id: MAIN_FIELD_TYPE.KI_TUSHO_MEI, label: '機種別' },
  { id: MAIN_FIELD_TYPE.TYPE, label: 'SISタイプ' },
  { id: MAIN_FIELD_TYPE.KITAG, label: '機種タグ別' },
] as const;

/**
 * 行の識別子を持つ param 名（API 契約）。
 * SISタイプ別は検索条件のタイプ絞込と同じ `sisTypes` を使うため、
 * グラフ取得ではチェック行の値で上書きする。
 */
export type ModelTransitionRowListKey = 'kiList' | 'sisTypes' | 'tagIds';

type ModelTransitionMainFieldMeta = {
  rowListKey: ModelTransitionRowListKey;
  seriesType: ModelTransitionGraphSeriesType;
  label: string;
};

const KI_MAIN_FIELD_META: ModelTransitionMainFieldMeta = {
  rowListKey: 'kiList',
  seriesType: 'ki',
  label: '機種',
};

const TYPE_MAIN_FIELD_META: ModelTransitionMainFieldMeta = {
  rowListKey: 'sisTypes',
  seriesType: 'type',
  label: 'SISタイプ',
};

const KITAG_MAIN_FIELD_META: ModelTransitionMainFieldMeta = {
  rowListKey: 'tagIds',
  seriesType: 'kiTag',
  label: '機種タグ',
};

export type ModelTransitionRowQueryParameter =
  DataModelTransitionData['rows'][number]['queryParameter'];

export const resolveModelTransitionMainField = (
  mainField?: MainFieldType
): MainFieldType => mainField ?? MAIN_FIELD_TYPE.KI_TUSHO_MEI;

const resolveModelTransitionMainFieldMeta = (
  mainField?: MainFieldType
): ModelTransitionMainFieldMeta => {
  const resolved = resolveModelTransitionMainField(mainField);
  if (resolved === MAIN_FIELD_TYPE.TYPE) {
    return TYPE_MAIN_FIELD_META;
  }
  if (resolved === MAIN_FIELD_TYPE.KITAG) {
    return KITAG_MAIN_FIELD_META;
  }
  return KI_MAIN_FIELD_META;
};

export const getModelTransitionRowListKey = (
  mainField?: MainFieldType
): ModelTransitionRowListKey =>
  resolveModelTransitionMainFieldMeta(mainField).rowListKey;

/**
 * 展開行（店舗別）の非推移項目を作る。
 *
 * 展開行の mainField は常に hlMei（店舗名）なので、メイン表の出力列
 * （kiTushoMei / type / kiTag）が非推移に残っていると店舗名が出ない。
 * 出力列だけ hlMei に差し替え、それ以外の表示項目はそのまま残す。
 */
export const buildModelTransition2ndRowNonTransitiveFields = (
  nonTransitiveFields: string[],
  mainField?: MainFieldType
): string[] => {
  const outputColumnCode = resolveModelTransitionMainField(mainField);
  const replaced = nonTransitiveFields.map((code) =>
    code === outputColumnCode ? MAIN_FIELD_TYPE.HL_MEI : code
  );
  const deduped = [...new Set(replaced)];
  return deduped.length === 0 ? [MAIN_FIELD_TYPE.HL_MEI] : deduped;
};

/**
 * 出力に応じた非推移項目のデフォルトを返す。
 *
 * - 機種別: settings/options の isDefault（通常 type / donyuDay）
 * - SISタイプ・機種タグ: 空配列
 *   （fetch 時に空配列は mainField 列のみへ変換されるため、タイプ・導入日は出さない）
 *
 * isDefault の定義元は API（settings/options）。適用・出力別の上書きはフロント側。
 */
export const resolveModelTransitionDefaultNonTransitiveFields = (
  mainField: MainFieldType | undefined,
  nonTransitionOptions: ReadonlyArray<{ code: string; isDefault?: boolean }>
): string[] => {
  const resolved = resolveModelTransitionMainField(mainField);
  if (resolved === MAIN_FIELD_TYPE.TYPE || resolved === MAIN_FIELD_TYPE.KITAG) {
    return [];
  }
  return nonTransitionOptions
    .filter((field) => field.isDefault)
    .map((field) => field.code);
};

/**
 * 出力（mainField）で選択できる表示項目かどうか。
 *
 * settings/options の `unsupportedMainFields` と、出力と同じ code の項目
 * （例: SISタイプ別の type 列）を非活性にする。機種集計の checkableFields と同じ判定。
 */
export const isModelTransitionFieldSupportedForMainField = (
  field: Pick<Option, 'code' | 'unsupportedMainFields'>,
  mainField?: MainFieldType
): boolean => {
  const resolved = resolveModelTransitionMainField(mainField);
  if (field.code === resolved) {
    return false;
  }
  return field.unsupportedMainFields?.includes(resolved) !== true;
};

/**
 * 表示項目パネルでチェック可能な項目（非対応の項目は残して非活性にする）
 */
export const filterModelTransitionCheckableFields = (
  fields: readonly Option[],
  mainField?: MainFieldType
): Option[] =>
  fields.filter((field) =>
    isModelTransitionFieldSupportedForMainField(field, mainField)
  );

/**
 * 出力変更時に、選択中の表示項目から非対応 code を除く。
 * 残らなければ isDefault かつ対応している項目へ戻す。
 */
export const sanitizeModelTransitionSelectedFieldCodes = (
  selectedCodes: string[] | undefined,
  fieldOptions: readonly Option[],
  mainField?: MainFieldType
): string[] | undefined => {
  if (selectedCodes === undefined) {
    return undefined;
  }

  const checkableFields = filterModelTransitionCheckableFields(
    fieldOptions,
    mainField
  );
  const supportedCodes = new Set(checkableFields.map((field) => field.code));
  const kept = selectedCodes.filter((code) => supportedCodes.has(code));
  if (kept.length > 0) {
    return kept;
  }

  return checkableFields
    .filter((field) => field.isDefault === true)
    .map((field) => field.code);
};

export const getModelTransitionRowId = (
  queryParameter: Partial<ModelTransitionRowQueryParameter>,
  mainField?: MainFieldType
): string | undefined => {
  const rowIds = queryParameter[getModelTransitionRowListKey(mainField)];
  return rowIds?.at(0);
};

export const resolveModelTransitionSeriesType = (
  mainField?: MainFieldType
): ModelTransitionGraphSeriesType =>
  resolveModelTransitionMainFieldMeta(mainField).seriesType;

export const resolveModelTransitionMainFieldFromSeriesType = (
  seriesType?: ModelTransitionGraphSeriesType
): MainFieldType => {
  if (seriesType === TYPE_MAIN_FIELD_META.seriesType) {
    return MAIN_FIELD_TYPE.TYPE;
  }
  if (seriesType === KITAG_MAIN_FIELD_META.seriesType) {
    return MAIN_FIELD_TYPE.KITAG;
  }
  return MAIN_FIELD_TYPE.KI_TUSHO_MEI;
};

export const buildModelTransitionRowListParams = (
  mainField: MainFieldType,
  rowIds: string[]
): Pick<DataModelTransitionParams, ModelTransitionRowListKey> => {
  const listKey = getModelTransitionRowListKey(mainField);
  return { [listKey]: rowIds };
};

const toNonEmptyStringList = (
  list: string[] | undefined
): string[] | undefined => (list != null && list.length > 0 ? list : undefined);

/**
 * SISタイプ別の支持率キー。検索の機種・タイプを分子・分母に載せ、kiListForSijiritu は含めない。
 * グラフと店舗展開で同じ載せ分けにする。
 */
export const buildModelTransitionTypeSeriesSijirituParams = (
  searchCondition: Pick<DataModelTransitionParams, 'kiList' | 'sisTypes'>
): Pick<DataModelTransitionParams, 'kiList' | 'sisTypesForSijiritu'> => {
  const searchKiList = toNonEmptyStringList(searchCondition.kiList);
  const searchSisTypes = toNonEmptyStringList(searchCondition.sisTypes);
  return {
    ...(searchKiList !== undefined ? { kiList: searchKiList } : {}),
    ...(searchSisTypes !== undefined
      ? { sisTypesForSijiritu: searchSisTypes }
      : {}),
  };
};

export const buildModelTransitionGraphParamsFromCheckedRows = (
  mainField: MainFieldType | undefined,
  checkedRowIds: string[],
  searchCondition: DataModelTransitionParams,
  fields?: string[]
): DataModelTransitionGraphParams => {
  const resolvedMainField = resolveModelTransitionMainField(mainField);
  const listParams = buildModelTransitionRowListParams(
    resolvedMainField,
    checkedRowIds
  );
  const isTypeSeries = resolvedMainField === MAIN_FIELD_TYPE.TYPE;
  const isKiTagSeries = resolvedMainField === MAIN_FIELD_TYPE.KITAG;
  const searchKiList = toNonEmptyStringList(searchCondition.kiList);

  return {
    seriesType: resolveModelTransitionSeriesType(resolvedMainField),
    ...(isTypeSeries
      ? buildModelTransitionTypeSeriesSijirituParams(searchCondition)
      : {
          kiListForSijiritu: searchCondition.kiListForSijiritu,
          ...(isKiTagSeries && searchKiList !== undefined
            ? { kiList: searchKiList }
            : {}),
        }),
    shuList: searchCondition.shuList,
    shuGroupIds: searchCondition.shuGroupIds,
    halls: searchCondition.halls,
    dateType:
      searchCondition.dateType !== undefined &&
      isGraphDateType(searchCondition.dateType)
        ? searchCondition.dateType
        : undefined,
    excludeToday: searchCondition.excludeToday,
    startDate: searchCondition.startDate,
    endDate: searchCondition.endDate,
    areas: searchCondition.areas,
    sisTypes: searchCondition.sisTypes,
    dayType: searchCondition.dayType,
    dateSuffixes: searchCondition.dateSuffixes,
    days: searchCondition.days,
    dates: searchCondition.dates,
    ...(fields !== undefined ? { fields } : {}),
    // SISタイプ別は sisTypes が行の識別子も兼ねるため、検索条件のタイプ絞込より後に置く
    ...listParams,
  };
};

export const getModelTransitionCheckedFilterLabel = (
  mainField?: MainFieldType
): string =>
  `チェック${resolveModelTransitionMainFieldMeta(mainField).label}絞込`;

export const getModelTransitionCheckedRowDescription = (
  mainField?: MainFieldType
): string => {
  const label = resolveModelTransitionMainFieldMeta(mainField).label;
  return `チェックを入れた${label}のみを表示します`;
};

export const getModelTransitionCheckedGraphDescription = (
  mainField?: MainFieldType
): string => {
  const label = resolveModelTransitionMainFieldMeta(mainField).label;
  return `チェックを入れた${label}の推移グラフを表示します`;
};

export const getModelTransitionGraphSelectorLabel = (
  mainField?: MainFieldType
): string => resolveModelTransitionMainFieldMeta(mainField).label;

export const getModelTransitionGraphTitle = (
  mainField?: MainFieldType
): string =>
  `推移グラフ（${resolveModelTransitionMainFieldMeta(mainField).label}比較）`;

export const getModelTransitionGraphSelectorEmptyLabel = (
  mainField?: MainFieldType
): string => `該当${resolveModelTransitionMainFieldMeta(mainField).label}なし`;

export const getModelTransitionGraphSelectorCountLabel = (
  mainField?: MainFieldType,
  count?: number
): string | undefined => {
  if (count === undefined || count <= 1) {
    return undefined;
  }
  const label = resolveModelTransitionMainFieldMeta(mainField).label;
  return `${count}${label}選択`;
};

export const getGraphCheckedRowIdsFromSetting = (
  setting: DataModelTransitionGraphSetting | undefined
): string[] => {
  if (!setting) {
    return [];
  }
  const mainField = resolveModelTransitionMainFieldFromSeriesType(
    setting.seriesType
  );
  return setting[getModelTransitionRowListKey(mainField)] ?? [];
};

/**
 * テーブル・グラフ・展開行 fetch に載せる halls。
 * 店舗ドロップダウン（currentHalls）が空のときは検索条件の halls を使う。
 */
export const resolveModelTransitionFetchHalls = (
  currentHalls: string[] | undefined,
  searchedHalls: string[] | undefined
): string[] | undefined =>
  currentHalls != null && currentHalls.length > 0
    ? currentHalls
    : searchedHalls;

/**
 * テーブルの種別選択（currentShu）を検索条件の shuList / shuGroupIds に載せる。
 * 種別ドロップダウン変更後も UI の searchCondition は古い種別のままなので、
 * グラフをお気に入り復元・再検索するときはテーブル fetch と同じ上書きが必要。
 */
export const applyModelTransitionCurrentShuToSearchCondition = (
  searchCondition: DataModelTransitionParams,
  currentShu: ShuOption
): DataModelTransitionParams => ({
  ...searchCondition,
  ...selectShus2HallReportSearchCondition([currentShu]),
});

/**
 * 表示中グラフの系列・行IDを保ったまま、最新の検索条件へ同期する。
 * 出力に対応しない行リスト・支持率分母キーは除き、再 fetch 経路ごとのパラメータ差異を防ぐ。
 * containsAverage は API エコー／古いお気に入りの残りなので送らない（ボタン経路と同じ）。
 */
export const syncModelTransitionGraphParamsWithSearchCondition = (
  graphParams: DataModelTransitionGraphParams,
  searchCondition: DataModelTransitionParams
): DataModelTransitionGraphParams => {
  const {
    kiList: _kiList,
    sisTypes: _sisTypes,
    tagIds: _tagIds,
    kiListForSijiritu: _kiListForSijiritu,
    sisTypesForSijiritu: _sisTypesForSijiritu,
    containsAverage: _containsAverage,
    ...paramsWithoutRowLists
  } = graphParams;
  const mainField = resolveModelTransitionMainField(searchCondition.mainField);
  const checkedRowIds = getGraphCheckedRowIdsFromSetting(graphParams);

  return {
    ...paramsWithoutRowLists,
    ...buildModelTransitionGraphParamsFromCheckedRows(
      mainField,
      checkedRowIds,
      searchCondition,
      graphParams.fields
    ),
  };
};
